import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Images } from 'lucide-react';
import { ApiError, api } from '../api/client.ts';
import { apiOrigin, isNative, nativePlatform, sessionToken } from '../api/origin.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { reportClientError } from '../lib/errorReporter.ts';
import {
  CONSENT_VERSION,
  IDLE_STATUS,
  PLUGIN_TIMEOUT_MS,
  confirmDeviceRegistration,
  galleryDeviceId,
  galleryPlugin,
  remintGalleryDeviceId,
  statusLine,
  withTimeout,
  type GalleryPlugin,
  type GalleryStatus,
} from '../lib/gallery.ts';
import {
  formatGalleryLine,
  formatGalleryLog,
  galleryLog,
  galleryLogAll,
  galleryLogSnapshot,
  galleryPluginMethods,
  logBridgeReport,
  subscribeGalleryLog,
  traced,
} from '../lib/galleryDebug.ts';
import './SettingsScreen.css';
import './GalleryScreen.css';

/**
 * «Общая библиотека» — the camera roll, copied to this server.
 *
 * The screen exists rather than a switch in the list for one reason, and
 * docs/GALLERY.md is firm about it: the explanation has to be on screen
 * *before* any system dialog. iOS asks its question once and remembers the
 * answer forever, so a permission alert that arrives before the person knows
 * what they are being asked is the whole conversation, and «Не разрешать» is
 * the sensible reply to a question nobody explained.
 *
 * The text below therefore says the uncomfortable part out loud. It is the only
 * honest version, and this feature is only defensible among people who already
 * know each other.
 */

/** How often the screen asks the engine where it got to. */
const POLL_MS = 2000;

/**
 * A poll is a dictionary lookup on the other side, so it either answers at once
 * or the plugin is not there at all. Short, and deliberately shorter than the
 * interval, so the polls cannot pile up on a bridge that never replies.
 */
const POLL_TIMEOUT_MS = 1500;

export function GalleryScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<GalleryStatus>(IDLE_STATUS);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /** The native half is absent or not answering; see poll(). */
  const [silent, setSilent] = useState(false);
  /** Momentary «Скопировано» on the log panel. */
  const [copied, setCopied] = useState(false);
  /** Set once the device row exists, so the engine is only started after it. */
  const registered = useRef(false);

  /**
   * Everything the engine could not do, handed to the ordinary client-error
   * queue — which is the whole reporting story for this proof of concept.
   * `upload` is already a ClientErrorKind, so /admin groups these with the
   * failures that come from sending a photo, which is what they are.
   */
  const drain = useCallback(async () => {
    const plugin = galleryPlugin();
    if (!plugin) return;
    const { errors } = await plugin.drainErrors();
    for (const failure of errors) {
      reportClientError({
        kind: 'upload',
        detail: `gallery/${failure.detail}`,
        message: failure.message,
        status: failure.status ?? undefined,
      });
    }
  }, []);

  /** Said once rather than every two seconds; cleared when it works again. */
  const complained = useRef(false);

  /**
   * The engine's own account of what it has been doing, moved into the panel.
   *
   * This is what the panel is for now: «Включено» above cannot tell anybody why
   * nothing is arriving, and the lines that can — checked 500 against the
   * server, sent 25 previews, skipped 3 that live only in iCloud, waited 30 s on
   * a slow link — are written on the Swift side, where the work happens. See
   * `drainEvents` in GallerySync.swift.
   *
   * Guarded by the plugin's announced method list rather than by trying it: a
   * method this app does not have is not refused by the bridge, it is never
   * answered at all, so calling it blind would cost a timeout every two seconds
   * on precisely the builds that are already broken.
   */
  const pullEvents = useCallback(async (plugin: GalleryPlugin) => {
    if (!galleryPluginMethods()?.includes('drainEvents')) {
      if (!complained.current) {
        complained.current = true;
        galleryLog('в этой сборке приложения нет журнала синхронизации', 'bridge');
      }
      return;
    }
    try {
      const { events } = await withTimeout(plugin.drainEvents(), POLL_TIMEOUT_MS, 'drainEvents');
      galleryLogAll(events.map((event) => ({ at: event.at, text: event.text })));
      complained.current = false;
    } catch (error) {
      if (!complained.current) {
        complained.current = true;
        galleryLog(`✕ журнал: ${error instanceof Error ? error.message : String(error)}`, 'call.fail');
      }
    }
  }, []);

  /**
   * The shape of the run as last polled — permission, and whether it is going.
   * Not the counts: those move every two seconds and the engine already reports
   * them when it actually sends something, so logging them here would push its
   * lines off the top of the panel with the same numbers.
   */
  const lastState = useRef('');

  const poll = useCallback(async () => {
    const plugin = galleryPlugin();
    if (!plugin) {
      // The same diagnosis the timeout below used to make, now made instantly
      // and with better evidence: on a native build the plugin is announced by
      // the shell before the page exists, so its absence *is* the answer — the
      // web bundle here is newer than the app around it.
      if (lastState.current !== 'нет плагина') {
        lastState.current = 'нет плагина';
        galleryLog('galleryPlugin() = null — Capacitor.Plugins.Gallery нет в этой сборке', 'bridge');
      }
      setSilent(true);
      return;
    }
    try {
      const next = await withTimeout(plugin.status(), POLL_TIMEOUT_MS, 'status');
      setStatus(next);
      setSilent(false);
      // Written on the first poll too, and that is deliberate: a library that
      // is already finished produces no events at all, and a panel that says
      // nothing is indistinguishable from one that is broken.
      const state = `${next.permission}/${next.running}/${next.paused}`;
      if (state !== lastState.current) {
        lastState.current = state;
        galleryLog(statusLine(next), 'status');
      }
      await pullEvents(plugin);
      await drain();
    } catch (error) {
      // The native half did not answer a question it cannot fail at. On this
      // screen that has one meaning worth telling somebody: the web bundle in
      // this app is newer than the code around it, which is what an install
      // that skipped a build looks like from the inside.
      const summary = `✕ status: ${error instanceof Error ? error.message : String(error)}`;
      if (summary !== lastState.current) {
        lastState.current = summary;
        galleryLog(summary, 'call.fail');
      }
      setSilent(true);
    }
  }, [drain, pullEvents]);

  useEffect(() => {
    if (!isNative) return;
    logBridgeReport();
    void poll();
    const timer = window.setInterval(() => void poll(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [poll]);

  /**
   * Agreeing, in one press: the system dialog, then the device row, then the
   * engine.
   *
   * **iOS is asked first, before anything touches the network.** The button
   * says «Разрешить доступ» and that is what pressing it must do — an earlier
   * arrangement registered the device first, so that a server which was slow,
   * unreachable or refusing left the press with no dialog and nothing to show
   * for it. Anything between the press and the alert is a way for the one
   * thing the button promises not to happen.
   *
   * The consent row is written afterwards and whatever iOS answered, because
   * consent is what was given *here*: somebody who agrees and then taps
   * «Выбрать фото» has still agreed, and the record is of the text they read.
   *
   * It is also the manual version of `useGallerySync`, which is why the button
   * above stays on screen once the access is `full`: the same three steps —
   * register, configure, start — done deliberately, by somebody who is watching
   * the panel underneath it.
   */
  async function enable() {
    setBusy(true);
    setMessage(null);
    galleryLog('— кнопка: спрашиваем доступ и запускаем проход —', 'click');
    try {
      const plugin = galleryPlugin();
      if (!plugin) {
        galleryLog('плагина нет, дальше идти некуда', 'click.fail');
        setMessage('Эта функция есть только в приложении');
        return;
      }

      const { permission } = await traced(
        'requestPermission',
        plugin.requestPermission(),
        PLUGIN_TIMEOUT_MS,
      );
      setStatus((previous) => ({ ...previous, permission }));

      let deviceId = galleryDeviceId();
      if (!registered.current) {
        try {
          await traced(
            'registerGalleryDevice',
            api.registerGalleryDevice(deviceId, CONSENT_VERSION, nativePlatform()),
            PLUGIN_TIMEOUT_MS,
          );
        } catch (error) {
          // The stored id belongs to an account that used this phone before —
          // see remintGalleryDeviceId. One mint and one retry, not a loop: a
          // second 409 on a freshly minted UUID is a different problem and
          // deserves to be seen.
          if (!(error instanceof ApiError) || error.status !== 409) throw error;
          galleryLog(
            'это устройство записано за другим аккаунтом — заводим новый идентификатор',
            'click.remint',
          );
          deviceId = remintGalleryDeviceId();
          await traced(
            'registerGalleryDevice',
            api.registerGalleryDevice(deviceId, CONSENT_VERSION, nativePlatform()),
            PLUGIN_TIMEOUT_MS,
          );
        }
        registered.current = true;
        // The durable twin of the ref above, for useGallerySync's launch-time
        // check — without it the next app open would pay the upsert again.
        if (user) confirmDeviceRegistration(user.id, deviceId);
      }
      if (permission !== 'full') {
        galleryLog(`остановились: доступ «${permission}», а движку нужен «full»`, 'click.fail');
        return;
      }

      // The token is handed over rather than read by the plugin: the engine
      // runs in a background task with no WebView to ask, so it needs its own
      // copy — see configure() in GalleryPlugin.swift.
      await traced(
        'configure',
        plugin.configure({
          origin: apiOrigin,
          token: sessionToken() ?? '',
          deviceId,
          userId: user?.id ?? '',
        }),
        PLUGIN_TIMEOUT_MS,
      );
      await traced('start', plugin.start({ source: 'manual' }), PLUGIN_TIMEOUT_MS);
      await poll();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Не удалось включить');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The same system dialog, asked with nothing else attached.
   *
   * Diagnostic only, and it is in the panel rather than in `enable()` because
   * the states that hide the button above are exactly the states worth pressing
   * from: it works while `silent` is true, while the access is `denied`, and
   * before any poll has answered — so «no dialog appeared» can be told apart
   * from «no button to press».
   */
  async function askDirectly() {
    galleryLog('— спрашиваем iOS напрямую —', 'click');
    const plugin = galleryPlugin();
    if (!plugin) {
      galleryLog('плагина нет', 'click.fail');
      return;
    }
    try {
      const answer = await traced('requestPermission', plugin.requestPermission(), PLUGIN_TIMEOUT_MS);
      setStatus((previous) => ({ ...previous, permission: answer.permission }));
    } catch {
      // traced() has already written the reason down, which is all this button
      // was for.
    }
  }

  async function openSettings() {
    const plugin = galleryPlugin();
    await plugin?.openSettings();
  }

  const { permission } = status;
  const debugLines = useSyncExternalStore(subscribeGalleryLog, galleryLogSnapshot, galleryLogSnapshot);


  async function copyLog() {
    try {
      await navigator.clipboard.writeText(formatGalleryLog());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard permission on this WebView; the text is on screen anyway
      // and a screenshot carries it just as well.
    }
  }

  return (
    <div className="app-frame settings-screen">
      <header className="settings-header">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => navigate('/settings')}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="settings-heading">Расширенные права (тест режим)</h2>
      </header>

      <div className="gallery-body">
        <div className="gallery-hero">
          <Images size={26} />
        </div>

        <p className="gallery-lead">
          Расширенные права нужны, чтобы не спрашивать разрешение на камеру каждый раз, а только один раз
        </p>
        <ul className="gallery-points">
         
        </ul>

        {/* Shown with the access already granted too, and that is not a spare
            button: `enable()` is also the only thing that hands the engine a
            fresh token and starts a run by hand, and hiding it left a phone
            whose sync had stopped with nothing to press. iOS answers an
            already-settled `requestPermission` instantly and without a dialog,
            so pressing it costs one round trip through the bridge. */}
        {!silent && (
          <button type="button" className="gallery-action" onClick={() => void enable()} disabled={busy}>
            {busy ? 'Спрашиваем…' : permission === 'full' ? 'Запустить проход' : 'Разрешить доступ'}
          </button>
        )}

        {/* The one diagnosis this screen can make about itself, and the one
            worth making: the page is newer than the app around it, so no
            dialog will ever appear however many times the button is pressed. */}
        {silent && (
          <div className="gallery-state gallery-state-warn">
            <p>
              Тест
            </p>
          </div>
        )}

        {/* The trap docs/GALLERY.md names: «Выбрать фото» is the prominent
            button in the iOS dialog, and a backup of four photographs looks
            exactly like one that is working. So it is said plainly, and the
            only way out of it is the Settings app. */}
        {permission === 'limited' && (
          <div className="gallery-state gallery-state-warn">
            <p>
              Открыт доступ только к выбранным фото. Так библиотека не соберётся — нужен полный
              доступ.
            </p>
            <button type="button" className="gallery-link" onClick={() => void openSettings()}>
              Открыть настройки iOS
            </button>
          </div>
        )}

        {(permission === 'denied' || permission === 'restricted') && (
          <div className="gallery-state gallery-state-warn">
            <p>Доступ к фото запрещён. Его можно вернуть в настройках телефона.</p>
            <button type="button" className="gallery-link" onClick={() => void openSettings()}>
              Открыть настройки iOS
            </button>
          </div>
        )}

        {/* One word, and no second sentence under it. Everything a progress
            line would have said — how many are left, whether it is waiting for
            a better link, when the last batch landed — is in the panel below,
            written by the engine at the moment it happened rather than guessed
            at from a status polled two seconds ago. */}
        {permission === 'full' && (
          <div className="gallery-state">
            <p className="gallery-enabled">Включено</p>
          </div>
        )}

        {permission === 'unavailable' && !isNative && (
          <div className="gallery-state">
            <p>Эта функция работает только в приложении для телефона.</p>
          </div>
        )}

        {message !== null && <div className="gallery-state gallery-state-warn">{message}</div>}

        {/* The output panel: what the engine did, in the order it did it. See
            lib/galleryDebug.ts for what each line is meant to distinguish.

            Admins only, because it is written for whoever is going to fix it:
            device ids, batch sizes, HTTP statuses and the reason a batch was
            skipped. For everybody else it is a wall of text under a switch
            that says «Включено», and the honest thing to do with a diagnostic
            nobody can act on is not to show it. The lines are still collected
            on every phone — the panel is only the window — so an admin
            account on the same device sees the whole run. */}
        {isNative && user?.isAdmin === true && (
          <div className="gallery-debug">
            <div className="gallery-debug-head">
              <span>Журнал синхронизации</span>
              <button type="button" className="gallery-link" onClick={() => void askDirectly()}>
                Спросить iOS
              </button>
              <button type="button" className="gallery-link" onClick={() => void copyLog()}>
                {copied ? 'Скопировано' : 'Скопировать'}
              </button>
            </div>
            {/* The newest line is the one worth seeing, and the panel is pinned
                to it by CSS rather than by a scroll written from an effect —
                see GalleryScreen.css. The lines live in a child of the
                scroller, which is what that arrangement needs. */}
            <pre className="gallery-debug-log">
              <div>{debugLines.map(formatGalleryLine).join('\n')}</div>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
